/**
 * VoiceConversationOverlay — full-screen hands-free conversation mode.
 *
 * UX model (vs the inline composer mic):
 *   • Inline mic   = "Dictate to compose"  → spoken text drops into the
 *                    composer textarea, user manually hits Send.
 *   • This overlay = "Hands-free voice"    → speak, 2 s silence auto-sends,
 *                    agent reply is read aloud AND shown on screen so
 *                    the user can read along, then mic re-arms for the
 *                    next turn. No keyboard, no clicking Send.
 *
 * Layout: stacked transcript bubbles (last user turn + agent reply) at the
 * top, big breathing mic in the centre, status + hint below. Backdrop
 * blur so the chat history bleeds through faintly behind.
 *
 * Wires up:
 *   onTranscript(text) — auto-fires when the user pauses for 2 s. Parent
 *                        feeds this into handleSend, which kicks the chat.
 *   onReady(speakFn)   — overlay hands its TTS speak() to the parent on
 *                        mount; the parent's messages effect calls it
 *                        whenever a fresh assistant message arrives so
 *                        the overlay's 'speaking' state stays in sync
 *                        with playback.
 */
import React, { useCallback, useEffect, useRef } from 'react';
import { useVoice } from '../../hooks/useVoice';
import type { ChatMessage } from '../../api/chat';

interface Props {
  /** Recent chat history — overlay shows the last user + last agent
   *  messages so the user can read along during conversation mode. */
  messages: ChatMessage[];
  /** Called with the transcript when the user finishes speaking. */
  onTranscript: (text: string) => void;
  /** Called once with a `speak` function the parent can invoke when the
   *  agent reply text arrives. */
  onReady?: (speak: (text: string) => Promise<void>) => void;
  /** Close button → returns to the inline composer view. */
  onClose: () => void;
  /** Parent's `sending` state — drives the "Thinking…" status while the
   *  agent generates its reply. */
  thinking?: boolean;
}

export const VoiceConversationOverlay: React.FC<Props> = ({
  messages,
  onTranscript,
  onReady,
  onClose,
  thinking,
}) => {
  const voice = useVoice();
  const onTranscriptRef = useRef(onTranscript);
  onTranscriptRef.current = onTranscript;

  // Hand a speak function to the parent that re-arms listening when TTS
  // ends naturally — the user gets hands-free turn-taking without having
  // to tap the mic between every reply. We track 'closing' so a close-
  // mid-speak doesn't ghost-rearm right before unmount.
  const closingRef = useRef(false);
  const speakRef = useRef(voice.speak);
  speakRef.current = voice.speak;
  const startListeningRef = useRef(voice.startListening);
  startListeningRef.current = voice.startListening;
  useEffect(() => {
    const speakWithRearm = (text: string) =>
      speakRef.current(text, () => {
        if (closingRef.current) return;
        startListeningRef.current((t) => onTranscriptRef.current(t));
      });
    onReady?.(speakWithRearm);
  }, [onReady]);

  // ESC closes the overlay
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const isConnecting = voice.voiceState === 'requesting-mic';
  const isListening  = voice.voiceState === 'listening';
  const isSpeaking   = voice.voiceState === 'speaking';
  const isError      = voice.voiceState === 'error';
  const isIdle       = voice.voiceState === 'idle' || isError;

  const handleTap = useCallback(() => {
    if (isConnecting) return;
    if (isSpeaking) {
      voice.stopSpeaking();
      voice.startListening((t) => onTranscriptRef.current(t));
      return;
    }
    if (isIdle) {
      voice.startListening((t) => onTranscriptRef.current(t));
      return;
    }
    if (isListening) {
      const t = voice.stopListening();
      if (t.trim()) onTranscriptRef.current(t);
    }
  }, [voice, isConnecting, isSpeaking, isIdle, isListening]);

  const handleClose = useCallback(() => {
    closingRef.current = true;
    voice.cancelListening();
    voice.stopSpeaking();
    onClose();
  }, [voice, onClose]);

  // Show the last assistant + the user message that prompted it. We pick
  // the very last assistant message (or the last user message if it's
  // newer than the last agent message — i.e. the user just spoke and we're
  // waiting on a reply).
  const tail = (() => {
    if (!messages.length) return { lastUser: null, lastAgent: null } as const;
    let lastUser: ChatMessage | null = null;
    let lastAgent: ChatMessage | null = null;
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (!lastAgent && m.role === 'assistant') lastAgent = m;
      if (!lastUser && m.role === 'user') lastUser = m;
      if (lastAgent && lastUser) break;
    }
    // If the user message is newer than the last agent message, only
    // show the user bubble (we're mid-turn, agent hasn't replied yet).
    if (lastAgent && lastUser && new Date(lastUser.timestamp) > new Date(lastAgent.timestamp)) {
      return { lastUser, lastAgent: null } as const;
    }
    return { lastUser, lastAgent } as const;
  })();

  // Auto-scroll the transcript area as new content arrives
  const transcriptRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    transcriptRef.current?.scrollTo({ top: transcriptRef.current.scrollHeight, behavior: 'smooth' });
  }, [tail.lastAgent?.timestamp, tail.lastUser?.timestamp, voice.interimTranscript, thinking]);

  // "Thinking" must reflect ONLY a genuinely in-flight turn — the user has
  // spoken and no newer agent message exists yet. Keying the status/hint/
  // animation AND the mic's disabled state off the raw `thinking` prop froze
  // the status on "Thinking" under a delivered reply and KEPT THE MIC DISABLED
  // while the agent was speaking — so the IC couldn't barge in (Yash: "when
  // the agent is speaking I can't cut her off"). Same fix as prod frontend-cima.
  const awaitingReply = thinking && !tail.lastAgent;

  const statusLabel = (() => {
    if (isError)       return voice.error || 'Voice error';
    if (awaitingReply) return 'Thinking';
    if (isConnecting)  return 'Connecting';
    if (isListening)   return 'Listening';
    if (isSpeaking)    return 'Speaking';
    return 'Tap to speak';
  })();
  const statusAnimated = awaitingReply || isConnecting || isListening || isSpeaking;

  const hint = (() => {
    if (isError)      return voice.error || 'Tap to retry';
    if (awaitingReply) return 'Working on your last turn';
    if (isConnecting) return 'Opening microphone';
    if (isListening) {
      if (voice.isSilencePaused) return 'Paused — tap Resume when you’re ready to continue';
      return 'Speak now — auto-sends after a 5s pause. Hit Pause to take your time.';
    }
    if (isSpeaking)   return 'Tap to interrupt and speak';
    return 'Tap the button to start';
  })();

  // Countdown displayed on/around the mic while the silence timer is
  // running. Falls back to '—' when not counting (paused, pre-first-word).
  const silenceSecondsLeft = voice.silenceMsLeft != null
    ? Math.max(0, Math.ceil(voice.silenceMsLeft / 1000))
    : null;
  // Progress 0..1 of the countdown — drives the ring fill.
  const silenceProgress = voice.silenceMsLeft != null
    ? Math.max(0, Math.min(1, voice.silenceMsLeft / 5000))
    : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Voice conversation mode"
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(10, 16, 32, 0.82)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center',
        padding: '64px 24px 32px',
        animation: 'cimaOverlayFadeIn 220ms ease-out',
        gap: 18,
      }}
    >
      {/* Mute toggle (top-left) */}
      <button
        type="button"
        onClick={voice.toggleMute}
        aria-label={voice.isMuted ? 'Unmute agent voice' : 'Mute agent voice'}
        title={voice.isMuted ? 'Agent voice muted — click to enable' : 'Agent voice on — click to mute'}
        style={{
          position: 'absolute', top: 18, left: 18,
          width: 40, height: 40, borderRadius: '50%',
          border: 'none', cursor: 'pointer',
          background: 'rgba(255,255,255,0.12)',
          color: voice.isMuted ? 'rgba(255,255,255,0.5)' : '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {voice.isMuted ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
            <line x1="23" y1="9" x2="17" y2="15" />
            <line x1="17" y1="9" x2="23" y2="15" />
          </svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
            <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />
          </svg>
        )}
      </button>

      {/* Close (top-right) */}
      <button
        type="button"
        onClick={handleClose}
        aria-label="Close voice mode"
        title="Close (Esc)"
        style={{
          position: 'absolute', top: 18, right: 18,
          width: 40, height: 40, borderRadius: '50%',
          border: 'none', cursor: 'pointer',
          background: 'rgba(255,255,255,0.12)',
          color: '#fff', fontSize: 22, lineHeight: 1,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >×</button>

      {/* Title row */}
      <div style={{
        color: 'rgba(255,255,255,0.55)', fontSize: 11.5,
        textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 600,
      }}>
        Hands-free voice
      </div>

      {/* Conversation transcript — last user + last agent bubble */}
      <div
        ref={transcriptRef}
        style={{
          flex: 1, width: '100%', maxWidth: 620,
          overflowY: 'auto',
          display: 'flex', flexDirection: 'column', gap: 10,
          padding: '8px 4px',
          minHeight: 0,
        }}
      >
        {tail.lastUser && (
          <Bubble role="user" text={tail.lastUser.content} />
        )}
        {tail.lastAgent && (
          <Bubble
            role="assistant"
            text={tail.lastAgent.content}
            isMuted={voice.isMuted}
            onToggleMute={voice.toggleMute}
            isSpeaking={isSpeaking}
          />
        )}
        {/* Live interim transcript while user is speaking */}
        {(isListening || isConnecting) && voice.interimTranscript && (
          <Bubble role="user" text={voice.interimTranscript} interim />
        )}
        {thinking && !tail.lastAgent && (
          <Bubble role="assistant" text="" thinkingLabel />
        )}
      </div>

      {/* Status text */}
      <div style={{
        color: 'rgba(255,255,255,0.92)', fontSize: 16, fontWeight: 500,
        textAlign: 'center', minHeight: 22, display: 'flex',
        alignItems: 'center', justifyContent: 'center', gap: 4,
      }}>
        <span>{statusLabel}</span>
        {statusAnimated && <DotsAnim />}
      </div>

      {/* Mic + (when listening) Pause / countdown row.
          Mic stays centred; the Pause button sits to its right and the
          countdown number/ring draws around the mic when silence is
          counting down. The whole row only renders the pause + countdown
          while listening so the idle/speaking states stay clean. */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, position: 'relative' }}>
        {/* Spacer matching the Pause button's width so the mic stays
            visually centred regardless of whether Pause is mounted. */}
        <div style={{ width: 56, height: 56, visibility: 'hidden' }} aria-hidden />

        {/* Mic with optional SVG countdown ring overlay */}
        <div style={{ position: 'relative', width: 96, height: 96 }}>
          {isListening && silenceProgress != null && !voice.isSilencePaused && (
            <svg
              width={108} height={108}
              viewBox="0 0 108 108"
              style={{ position: 'absolute', top: -6, left: -6, pointerEvents: 'none', transform: 'rotate(-90deg)' }}
              aria-hidden="true"
            >
              <circle
                cx="54" cy="54" r="51"
                stroke="rgba(255,255,255,0.10)" strokeWidth="3" fill="none"
              />
              {/* progress = msLeft/total → stroke-dasharray draws the
                  remaining wedge. Colour shifts from cool to hot as we
                  approach zero so the user feels the urgency. */}
              <circle
                cx="54" cy="54" r="51"
                stroke={silenceProgress > 0.5 ? '#10b981' : silenceProgress > 0.25 ? '#f59e0b' : '#ef4444'}
                strokeWidth="3" fill="none" strokeLinecap="round"
                strokeDasharray={`${2 * Math.PI * 51}`}
                strokeDashoffset={`${2 * Math.PI * 51 * (1 - silenceProgress)}`}
                style={{ transition: 'stroke-dashoffset 0.18s linear, stroke 0.3s' }}
              />
            </svg>
          )}
          <button
            type="button"
            onClick={handleTap}
            aria-label={isListening ? 'Listening — tap to send' : 'Tap to speak'}
            disabled={awaitingReply}
            style={{
              width: 96, height: 96, borderRadius: '50%',
              border: '3px solid rgba(255,255,255,0.32)',
              background: isListening
                ? '#ea4335'
                : isSpeaking
                  ? 'rgba(59, 130, 246, 0.22)'
                  : 'rgba(255,255,255,0.10)',
              color: '#fff',
              cursor: awaitingReply ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              touchAction: 'none', userSelect: 'none',
              WebkitTapHighlightColor: 'transparent',
              transition: 'background 0.2s, border-color 0.2s',
              animation: isListening && !voice.isSilencePaused
                ? 'cimaOverlayListenPulse 1.4s infinite'
                : isSpeaking
                  ? 'cimaOverlaySpeakBreathe 2s ease-in-out infinite'
                  : 'none',
              boxShadow: isSpeaking ? '0 0 24px 6px rgba(59,130,246,0.18)' : 'none',
              opacity: awaitingReply ? 0.6 : 1,
            }}
          >
            {isSpeaking ? (
              <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                <path d="M19.07 4.93a10 10 0 010 14.14" />
                <path d="M15.54 8.46a5 5 0 010 7.07" />
              </svg>
            ) : (
              <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="2" width="6" height="12" rx="3" />
                <path d="M5 10a7 7 0 0014 0" />
                <line x1="12" y1="17" x2="12" y2="22" />
                <line x1="8" y1="22" x2="16" y2="22" />
              </svg>
            )}
          </button>
          {/* Centred countdown number — only while actively counting */}
          {isListening && silenceSecondsLeft != null && silenceSecondsLeft > 0 && !voice.isSilencePaused && (
            <div
              aria-live="polite"
              style={{
                position: 'absolute', bottom: -28, left: 0, right: 0,
                textAlign: 'center', fontSize: 11, fontWeight: 700,
                color: 'rgba(255,255,255,0.85)', letterSpacing: 0.4,
                textShadow: '0 1px 2px rgba(0,0,0,0.4)',
              }}
            >
              auto-send in {silenceSecondsLeft}s
            </div>
          )}
        </div>

        {/* Pause / Resume — only mounted while listening so it doesn't
            take up space in the idle state. Tapping pauses the auto-send
            countdown without dropping the mic. */}
        {isListening ? (
          <button
            type="button"
            onClick={() => voice.isSilencePaused ? voice.resumeSilence() : voice.pauseSilence()}
            aria-label={voice.isSilencePaused ? 'Resume auto-send' : 'Pause auto-send'}
            title={voice.isSilencePaused ? 'Resume — auto-send will fire 5s after you stop talking' : 'Pause auto-send so you can think'}
            style={{
              width: 56, height: 56, borderRadius: '50%',
              border: '2px solid rgba(255,255,255,0.32)',
              background: voice.isSilencePaused ? 'rgba(16,185,129,0.22)' : 'rgba(255,255,255,0.10)',
              color: '#fff', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background 0.2s, border-color 0.2s',
            }}
          >
            {voice.isSilencePaused ? (
              // Resume = play triangle
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                <polygon points="6 4 20 12 6 20 6 4" />
              </svg>
            ) : (
              // Pause = two bars
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                <rect x="6" y="4" width="4" height="16" rx="1" />
                <rect x="14" y="4" width="4" height="16" rx="1" />
              </svg>
            )}
          </button>
        ) : (
          <div style={{ width: 56, height: 56, visibility: 'hidden' }} aria-hidden />
        )}
      </div>

      {/* Hint */}
      <div style={{
        color: 'rgba(255,255,255,0.55)', fontSize: 12.5,
        textAlign: 'center', maxWidth: 480,
      }}>
        {hint}
      </div>

      <style>{`
        @keyframes cimaOverlayFadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes cimaOverlayListenPulse {
          0%   { box-shadow: 0 0 0 0    rgba(234,67,53,0.45); }
          70%  { box-shadow: 0 0 0 22px rgba(234,67,53,0); }
          100% { box-shadow: 0 0 0 0    rgba(234,67,53,0); }
        }
        @keyframes cimaOverlaySpeakBreathe {
          0%, 100% { transform: scale(1);    opacity: 0.85; }
          50%      { transform: scale(1.06); opacity: 1; }
        }
        @keyframes cimaOverlayThinkingDot {
          0%, 80%, 100% { opacity: 0.25; transform: translateY(0); }
          40%           { opacity: 1;    transform: translateY(-2px); }
        }
      `}</style>
    </div>
  );
};

/* ── Sub-components ──────────────────────────────────────────────────── */

const Bubble: React.FC<{
  role: 'user' | 'assistant';
  text: string;
  interim?: boolean;
  thinkingLabel?: boolean;
  /** Assistant only — toggle TTS mute for the agent voice. */
  isMuted?: boolean;
  onToggleMute?: () => void;
  /** Assistant only — true while this reply is actively being spoken,
   *  used to colour the speaker icon. */
  isSpeaking?: boolean;
}> = ({ role, text, interim, thinkingLabel, isMuted, onToggleMute, isSpeaking }) => {
  const isUser = role === 'user';
  return (
    <div style={{
      display: 'flex',
      flexDirection: isUser ? 'row' : 'column',
      justifyContent: isUser ? 'flex-end' : 'flex-start',
      alignItems: isUser ? 'flex-end' : 'flex-start',
      gap: isUser ? 8 : 5,
      width: '100%',
    }}>
      {/* Agent attribution row — "CIMA Agent" label + inline mute toggle
          (tap to silence/restart TTS), mirroring the inline chat's agent
          prose header. */}
      {!isUser && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#7db3ff', letterSpacing: 0.2 }}>CIMA Agent</span>
          {onToggleMute && (
            <button
              type="button"
              onClick={onToggleMute}
              aria-label={isMuted ? 'Unmute agent voice' : 'Mute agent voice'}
              title={isMuted ? 'Agent voice muted — tap to enable' : 'Agent voice on — tap to mute'}
              style={{
                flex: '0 0 auto', width: 22, height: 22, borderRadius: '50%', border: 'none',
                background: isSpeaking && !isMuted ? 'rgba(59,130,246,0.32)' : 'rgba(255,255,255,0.08)',
                color: isMuted ? 'rgba(255,255,255,0.45)' : '#fff',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'background 0.15s, color 0.15s',
              }}
            >
              {isMuted ? (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                  <line x1="23" y1="9" x2="17" y2="15" />
                  <line x1="17" y1="9" x2="23" y2="15" />
                </svg>
              ) : (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                  <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />
                </svg>
              )}
            </button>
          )}
        </div>
      )}
      <div style={{
        maxWidth: isUser ? '80%' : '100%',
        padding: isUser ? '10px 15px' : '0',
        borderRadius: 16,
        fontSize: 14.5, lineHeight: isUser ? 1.55 : 1.6,
        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        color: isUser ? '#fff' : 'rgba(255,255,255,0.92)',
        background: isUser ? 'rgba(255,255,255,0.12)' : 'transparent',
        border: 'none',
        opacity: interim ? 0.7 : 1,
        fontStyle: interim ? 'italic' : 'normal',
      }}>
        {thinkingLabel ? <DotsAnim /> : (text || '\u00a0')}
      </div>
    </div>
  );
};

const DotsAnim: React.FC = () => (
  <span style={{ display: 'inline-flex', gap: 3 }}>
    <Dot delay={0} />
    <Dot delay={0.18} />
    <Dot delay={0.36} />
  </span>
);

const Dot: React.FC<{ delay: number }> = ({ delay }) => (
  <span style={{
    width: 5, height: 5, borderRadius: '50%',
    background: 'currentColor',
    display: 'inline-block',
    animation: `cimaOverlayThinkingDot 1.2s ease-in-out ${delay}s infinite`,
  }} />
);
